import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Settings,
  Save,
  RefreshCw,
  Eye,
  Video,
  Building,
  Phone,
  FileText,
  Globe
} from 'lucide-react';

interface SiteContent {
  [key: string]: string;
}

const SettingsPage: React.FC = () => {
  const [content, setContent] = useState<SiteContent>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('key, value');

      if (error) throw error;

      const contentData: SiteContent = {};
      data?.forEach((item) => {
        contentData[item.key] = item.value;
      });

      setContent(contentData);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateContent = (key: string, value: string) => {
    setContent(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveContent = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      // Update each content item individually
      const updatePromises = Object.entries(content).map(async ([key, value]) => {
        const { error } = await supabase
          .from('site_content')
          .update({ value })
          .eq('key', key);

        if (error) throw error;
      });

      await Promise.all(updatePromises);

      setSaveMessage('✅ Contenido guardado exitosamente');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving content:', error);
      setSaveMessage('❌ Error al guardar el contenido');
    } finally {
      setIsSaving(false);
    }
  };

  const previewSite = () => {
    window.open('/', '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  const sections = [
    {
      id: 'hero',
      title: 'Sección Héroe',
      icon: Globe,
      fields: [
        { key: 'Título de héroe', label: 'Título Principal', type: 'text' },
        { key: 'Subtítulo de héroe', label: 'Subtítulo', type: 'textarea' },
        { key: 'Llamada a héroe', label: 'Texto del Botón', type: 'text' },
      ]
    },
    {
      id: 'video',
      title: 'Sección de Video',
      icon: Video,
      fields: [
        { key: 'video_url', label: 'URL del Video (YouTube, Vimeo, etc.)', type: 'url' },
        { key: 'Título del video', label: 'Título de la Sección de Video', type: 'text' },
        { key: 'Descripción del video', label: 'Descripción del Video', type: 'textarea' },
      ]
    },
    {
      id: 'services',
      title: 'Servicios',
      icon: Settings,
      fields: [
        { key: 'Título del Servicio de Carnicería', label: 'Título Carnicería', type: 'text' },
        { key: 'Servicio de Carnicería Desc', label: 'Descripción Carnicería', type: 'textarea' },
        { key: 'Título del restaurante de servicio', label: 'Título Restaurante', type: 'text' },
        { key: 'Servicio Restaurante Desc', label: 'Descripción Restaurante', type: 'textarea' },
        { key: 'Título del negocio de servicio', label: 'Título Negocio', type: 'text' },
        { key: 'Descripción del negocio de servicio', label: 'Descripción Negocio', type: 'textarea' },
      ]
    },
    {
      id: 'about',
      title: 'Acerca de la Empresa',
      icon: Building,
      fields: [
        { key: 'Acerca del título', label: 'Título de la Sección', type: 'text' },
        { key: 'Acerca del contenido 1', label: 'Contenido Principal', type: 'textarea' },
        { key: 'Acerca del contenido 2', label: 'Contenido Secundario', type: 'textarea' },
        { key: 'Acerca del contenido 3', label: 'Contenido Terciario', type: 'textarea' },
        { key: 'Acerca del contenido 4', label: 'Contenido Adicional', type: 'textarea' },
      ]
    },
    {
      id: 'contact',
      title: 'Sección de Contacto',
      icon: Phone,
      fields: [
        { key: 'Título del contacto', label: 'Título de Contacto', type: 'text' },
        { key: 'Subtítulo de contacto', label: 'Subtítulo de Contacto', type: 'textarea' },
      ]
    },
    {
      id: 'footer',
      title: 'Pie de Página',
      icon: FileText,
      fields: [
        { key: 'footer_company_name', label: 'Nombre de la Empresa', type: 'text' },
        { key: 'Nombre de la empresa en el pie de página', label: 'Nombre Alternativo', type: 'text' },
        { key: 'footer_description', label: 'Descripción de la Empresa', type: 'textarea' },
        { key: 'Descripción del pie de página', label: 'Descripción Alternativa', type: 'textarea' },
        { key: 'footer_address', label: 'Dirección', type: 'textarea' },
        { key: 'Dirección del pie de página', label: 'Dirección Alternativa', type: 'textarea' },
        { key: 'footer_phone', label: 'Teléfono', type: 'text' },
        { key: 'Pie de página Teléfono', label: 'Teléfono Alternativo', type: 'text' },
        { key: 'footer_email', label: 'Correo Electrónico', type: 'email' },
        { key: 'Correo electrónico del pie de página', label: 'Correo Alternativo', type: 'email' },
        { key: 'footer_hours', label: 'Horario de Atención', type: 'textarea' },
        { key: 'Horario del pie de página', label: 'Horario Alternativo', type: 'textarea' },
        { key: 'Pie de página Derechos de autor', label: 'Copyright', type: 'text' },
      ]
    },
    {
      id: 'products',
      title: 'Sección de Productos',
      icon: Settings,
      fields: [
        { key: 'Título del producto', label: 'Título de Productos', type: 'text' },
        { key: 'Subtítulo de productos', label: 'Subtítulo de Productos', type: 'textarea' },
        { key: 'Productos de vacío Título', label: 'Título Productos al Vacío', type: 'text' },
        { key: 'Productos de vacío Descripción', label: 'Descripción Productos al Vacío', type: 'textarea' },
      ]
    },
    {
      id: 'location',
      title: 'Sección de Ubicación',
      icon: Globe,
      fields: [
        { key: 'location_title', label: 'Título de la Ubicación', type: 'text' },
        { key: 'location_description', label: 'Descripción de la Ubicación', type: 'textarea' },
        { key: 'location_image_1', label: 'URL de Imagen 1', type: 'url' },
        { key: 'location_image_2', label: 'URL de Imagen 2', type: 'url' },
        { key: 'location_image_3', label: 'URL de Imagen 3', type: 'url' },
        { key: 'location_image_4', label: 'URL de Imagen 4', type: 'url' },
        { key: 'location_map_url', label: 'URL del Mapa (Google Maps embed)', type: 'url' },
        { key: 'location_address', label: 'Dirección Completa', type: 'textarea' },
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configuración del Sitio</h1>
          <p className="mt-2 text-gray-600">Administra todo el contenido de la página de inicio</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={previewSite}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Eye className="w-4 h-4 mr-2" />
            Ver Sitio
          </button>
          <button
            onClick={loadContent}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Recargar
          </button>
        </div>
      </div>

      {saveMessage && (
        <div className={`p-4 rounded-lg ${saveMessage.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {saveMessage}
        </div>
      )}

      <div className="space-y-8">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center">
                  <Icon className="w-6 h-6 text-red-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {section.fields.map((field) => (
                    <div key={field.key} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {field.label}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea
                          value={content[field.key] || ''}
                          onChange={(e) => updateContent(field.key, e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-vertical"
                          placeholder={`Ingresa ${field.label.toLowerCase()}`}
                        />
                      ) : (
                        <input
                          type={field.type}
                          value={content[field.key] || ''}
                          onChange={(e) => updateContent(field.key, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder={`Ingresa ${field.label.toLowerCase()}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex justify-end">
          <button
            onClick={saveContent}
            disabled={isSaving}
            className="inline-flex items-center px-6 py-3 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Guardar Todos los Cambios
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;